import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import SelectNew from "components/select/SelectNew";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetDepartments, IDepartments } from "api/rpc/2024-04/facility";
import { capitalize } from "helpers/Helpers";
import { ButtonNew } from "components/buttonNew";
import Select from "components/select/Select";
import Option from "components/select/Option";
import classNames from "classnames";

interface IDiscountDepartmentSelectProps {
  stateCallback: (id: string, products: IDepartments[]) => void;
  onApplicationSelect: <T extends Record<string, any>>(updated: T, id: string) => void;
  discountDepartmentState: IDepartments[];
}

export default function DiscountDepartmentSelect(props: IDiscountDepartmentSelectProps) {
  const { stateCallback, onApplicationSelect, discountDepartmentState } = props;
  const { t } = useTranslation();
  const [departments, setDepartments] = useState<IDepartments[]>([]);
  const optionSelect = [
    {
      id: "included",
      label: t("Included"),
    },
    {
      id: "excluded",
      label: t("Excluded"),
    },
  ];

  useEffect(() => {
    void loadDepartments();
  }, []);

  async function loadDepartments() {
    const departmentRes = await GetDepartments(false);
    setDepartments(departmentRes.data);
  }

  function updateApplication(
    selectedApplication: { id: string; label: string },
    id: string,
    departmentIndex: number,
  ): void {
    const department = {
      ...discountDepartmentState[departmentIndex],
      pivot: {
        ...discountDepartmentState[departmentIndex].pivot,
        application: selectedApplication.id,
      },
    };
    discountDepartmentState[departmentIndex] = department;

    onApplicationSelect(discountDepartmentState, id);
  }

  function handleSelectDepartments(selectedDepartment: IDepartments) {
    let updated;

    if (discountDepartmentState?.some(dept => dept.id === selectedDepartment.id)) {
      updated = discountDepartmentState?.filter(dept => dept.id !== selectedDepartment.id);
    } else {
      const selected = {
        ...selectedDepartment,
        pivot: {
          ...selectedDepartment.pivot,
          application: "included",
        },
      };
      updated = [...discountDepartmentState, selected];
    }

    stateCallback("departments", updated);
  }

  function checkParentCount(department: IDepartments): number {
    if (!department.parent_id) {
      return 0;
    }

    const parent = departments.find(d => d.id === department.parent_id);

    if (!parent) {
      return 0;
    }

    return 1 + checkParentCount(parent);
  }

  function getPlaceholderTitles() {
    const departmentTitles = [];
    for (let i = 0; i < discountDepartmentState.length; i++) {
      departmentTitles.push(departments.find(type => type.id === discountDepartmentState[i].id)?.title ?? "");
    }
    return departmentTitles.join(", ");
  }

  return (
    <Card>
      <Card.Section>
        <Select
          label={t("Departments")}
          placeholder={discountDepartmentState?.length > 0 ? getPlaceholderTitles() : t("No Departments Selected")}
          onChange={(_, extraValue: IDepartments) => handleSelectDepartments(extraValue)}
        >
          {departments.map((department, departmentIndex) => {
            const depth = checkParentCount(department);
            return (
              <Option
                key={departmentIndex}
                value={department.title}
                extraValues={department}
                name="departments"
                className={classNames("department-option", `indent-${depth * 7}`)}
              >
                <span>{department.title}</span>
              </Option>
            );
          })}
        </Select>

        <DataTable
          title={t("Departments")}
          columns={[
            { label: t("Title") },
            { label: t("Type") },
            { label: t("Application") },
            { label: "", width: "10%" },
          ]}
          overflow
        >
          {discountDepartmentState.map((department, departmentIndex) => {
            return (
              <tr key={departmentIndex}>
                <td>{capitalize(department.title)}</td>
                <td>{capitalize(department.type)}</td>
                <td>
                  <SelectNew
                    id="application"
                    options={
                      optionSelect?.map(application => ({
                        id: application.id,
                        label: `${application.label}`,
                        extraValue: application,
                      })) ?? []
                    }
                    value={department.pivot?.application}
                    error={department.pivot?.application === null || department.pivot?.application === undefined}
                    onChange={(_, extraValue) => updateApplication(extraValue, "departments", departmentIndex)}
                  />
                </td>
                <td>
                  <ButtonNew type="text" onClick={() => handleSelectDepartments(department)}>
                    {t("Remove")}
                  </ButtonNew>
                </td>
              </tr>
            );
          })}
        </DataTable>
      </Card.Section>
    </Card>
  );
}
